import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {
  areBrandsInEmptyState,
  canUserAccessBrandKits,
  doesUserHaveMultipleBrands,
  doesUserHaveSingleBrand,
  getActiveBrandName,
  openAppropriateUpsellingDialogForBrandKits,
  shouldUpsellHalloweenBrandKit,
} from '@Libraries/brands-library';
import type {BrandAssetName} from '@Components/editor-brands/editor-brands.types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {changeActiveBrandId} from '@Components/mystuff-brands/brands-thunk';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {BrandsList} from '@Components/editor-brands/components/brands-list';
import {ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {noop} from '@Utils/general.util';
import styles from './brands-header.module.scss';
import {useAppDispatch} from '@/hooks';
import {useShowSeasonalBrandkitPopover} from '@Hooks/brands/useShowSeasonalBrandkitPopover';
import {IconWithTooltip} from '@Components/icon-with-tooltip/icon-with-tooltip';
import {SeasonalBrandkitPopover} from '@Components/editor-brands/components/seasonal-brandkit-popover/seasonal-brandkit-popover';
import {getCurrentSeasonName} from '@Libraries/seasons.library';
import {useSortedBrandKitsFromStore} from '@Hooks/brands/useSortedBrandKitsFromStore';
import {useActiveBrandId} from '@Hooks/brands/useActiveBrandId';
import {useBrandsLoadingState} from '@Hooks/brands/useBrandsLoadingState';
import type {BrandsLoadingState} from '@Components/mystuff-brands/brand-kits.types';
import {useBrandContentMap} from '@Hooks/brands/useBrandContentMap';

export interface BrandsHeaderProps {
  brandAsset: BrandAssetName;
  isSmallSection?: boolean;
  showSmallDropdownHeader?: boolean;
  showDropdownSelectorOnly?: boolean;
  onClickDropdownSelector?(): void;
}

const BrandsHeader = ({
  showSmallDropdownHeader = false,
  showDropdownSelectorOnly = false,
  onClickDropdownSelector = noop,
  ...props
}: BrandsHeaderProps): ReactElement<any> | null => {
  const activeBrandId: string = useActiveBrandId() ?? '';
  const brands = useSortedBrandKitsFromStore();
  const brandsLoadingState: BrandsLoadingState = useBrandsLoadingState();
  const brandContent = useBrandContentMap();
  const brandContentLoadingState = brandContent[activeBrandId] ? brandContent[activeBrandId][props.brandAsset].loaded : 0;
  const [showSeasonalBrandkitPopover, markUserHasSeenSeasonalBrandkitPopover] = useShowSeasonalBrandkitPopover(getCurrentSeasonName());
  const brandkitDropdownBoxRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();

  const changeActiveBrandForEditor = (val: string): void => {
    void dispatch(changeActiveBrandId({newActiveBrandId: val}));
  };

  const getSeasonalBrandkitPopover = (): ReactElement<any> | null => {
    if (!showSeasonalBrandkitPopover) {
      return null;
    }

    return (
      <SeasonalBrandkitPopover
        referenceElement={brandkitDropdownBoxRef}
        onMarkAsSeen={() => {
          void markUserHasSeenSeasonalBrandkitPopover();
        }}
      />
    );
  };

  const getBrandsHeader = (): ReactElement<any> => {
    return (
      <div>
        {showSeasonalBrandkitPopover ? getSeasonalBrandkitPopover() : null}

        <div ref={brandkitDropdownBoxRef} className={`flexbox ${styles.brandsHeader}`}>
          <div className="flex-row-align-center spacing-p-r-1 content-body">
            <Icon icon="icon-brands" size={IconSize.SIZE_ICON_16} className="spacing-p-r-1" />
            <Text val={areBrandsInEmptyState(brands) ? window.i18next.t('pmwjs_brand_kits') : getActiveBrandName(brands, activeBrandId)} size={TextSize.XSMALL} bold />
          </div>
          {!canUserAccessBrandKits() ? (
            <IconWithTooltip tooltipText={window.i18next.t('pmwjs_save_brand_colors')} icon="icon-crown" className="color-premium" size={IconSize.SIZE_ICON_20} />
          ) : (
            ''
          )}
        </div>
      </div>
    );
  };

  const onClickBrand = (brandId: string, openInModal = false): void => {
    if (shouldUpsellHalloweenBrandKit(brandId)) {
      openAppropriateUpsellingDialogForBrandKits();
      return;
    }

    changeActiveBrandForEditor(brandId);
    if (openInModal) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };

  const getSelectBox = (): ReactElement<any> => {
    return (
      <div>
        {showSeasonalBrandkitPopover ? getSeasonalBrandkitPopover() : null}

        <div ref={brandkitDropdownBoxRef}>
          <DropdownGhostSelectBox
            isBold
            isLoading={brandContentLoadingState === 2}
            textClassName={styles.brandsHeader}
            icon={`icon-brands ${styles.headerIcon}`}
            text={getActiveBrandName(brands, activeBrandId)}
            onClick={(): void => {
              if (showSeasonalBrandkitPopover) {
                void markUserHasSeenSeasonalBrandkitPopover();
              }
              onClickDropdownSelector();
            }}
          />
        </div>
      </div>
    );
  };

  const getBrandsDropdown = (): ReactElement<any> => {
    if (showDropdownSelectorOnly) {
      return getSelectBox();
    }
    return (
      <Dropdown
        className={styles.brandsDropdown}
        selector={getSelectBox()}
        position={DROPDOWN_POSITION.FIXED}
        popup={
          <BrandsList
            onClick={(id: string): void => {
              onClickBrand(id, false);
            }}
          />
        }
        popUpHasCustomWidth
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        popupClassName={`${styles.brandsPopupWidth} ${showSmallDropdownHeader ? styles.small : ''} border-s-standard`}
        maxHeight={showSmallDropdownHeader ? 250 : undefined}
        showModalForSmallScreen={!props.isSmallSection}
        modalTitle={window.i18next.t('pmwjs_select_a_brand')}
        modalContent={
          <BrandsList
            size={ControlledListItemSize.MEDIUM}
            onClick={(id: string): void => {
              onClickBrand(id, true);
            }}
          />
        }
      />
    );
  };

  const getHeader = (): ReactElement<any> | null => {
    if (doesUserHaveSingleBrand(brands) || (areBrandsInEmptyState(brands, brandsLoadingState) && props.isSmallSection)) {
      return getBrandsHeader();
    }
    if (doesUserHaveMultipleBrands(brands)) {
      return getBrandsDropdown();
    }
    return null;
  };

  return getHeader();
};

export default BrandsHeader;
